import { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../context/auth';
import { GuidedTour, TourStep, isStepAvailable, whenNoTourActive } from './GuidedTour';
import { isTourSeen, markTourSeen } from './tourProgress';

/**
 * Guía de una pantalla: botón "Guía" (en el encabezado o como opción de un menú desplegable) que la abre cuando el usuario quiera. La primera vez que el
 * usuario entra a la pantalla se abre sola; al terminarla u omitirla no se vuelve a abrir sola (se guarda en su usuario).
 */
interface Props {
    id: string;             // código de la guía: cada pantalla recuerda por separado si ya se vio
    steps: TourStep[];      // los elementos se marcan en la pantalla con data-tour="..."
    variant?: 'button' | 'dropdown-item';
}

const AUTO_START_DELAY = 1500;  // deja que la pantalla cargue sus datos y que abra primero la guía general

export const PageTour = ({ id, steps, variant = 'button' }: Props) => {
    const { user } = useContext(AuthContext);
    const [open, setOpen] = useState(false);
    const [visibleSteps, setVisibleSteps] = useState<TourStep[]>([]);

    const start = useCallback(() => {
        setVisibleSteps(steps.filter(isStepAvailable));
        setOpen(true);
    }, [steps]);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let cancelWait = () => { };
        isTourSeen(user.id, id).then(seen => {
            if (seen || cancelled) return;
            timer = setTimeout(() => { cancelWait = whenNoTourActive(start); }, AUTO_START_DELAY);
        });
        return () => { cancelled = true; clearTimeout(timer); cancelWait(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, id]);

    const finish = () => {
        setOpen(false);
        if (user) markTourSeen(user.id, id);
    };

    return (
        <>
            {variant === 'dropdown-item' ? (
                <button type="button" className="dropdown-item" onClick={start}>
                    <i className="bi bi-question-circle me-2" /> Guía de esta pantalla
                </button>
            ) : (
                <button type="button" className="btn btn-outline-secondary" onClick={start} title="Ver la guía de esta pantalla">
                    <i className="bi bi-question-circle me-1" /> Guía
                </button>
            )}
            {/* Fuera del botón: dentro de un menú desplegable cerrado no se vería */}
            {createPortal(<GuidedTour open={open} steps={visibleSteps} onFinish={finish} />, document.body)}
        </>
    );
};
